import React from "react";
import { Link } from "react-router-dom";
import {
  FaEnvelope,
  FaPhoneAlt,
  FaMapMarkerAlt,
  FaChevronRight,
} from "react-icons/fa";
import logo from "../assets/logo-with-backgroud.png";

export default function Footer() {
  const exploreLinks = [
    { label: "Home", to: "/" },
    { label: "About Us", to: "/about" },
    { label: "Contact Us", to: "/contact" },
    { label: "FAQs", to: "/faq" },
  ];

  const serviceLinks = [
    { label: "Car Rental Booking", to: "/services/car-rental-booking" },
    { label: "Cancellation Support", to: "/services/car-rental-cancellation" },
    { label: "Rental Modifications", to: "/services/car-rental-modification" },
    { label: "Long-Term Rentals", to: "/services/long-term-car-rental" },
  ];

  const legalLinks = [
    { label: "Privacy Policy", to: "/privacy-policy" },
    { label: "Terms & Conditions", to: "/terms-and-conditions" },
  ];

  const handlePhoneClick = () => {
    if (window.gtag) {
      window.gtag("config", "AW-18343552397", {
        phone_conversion_number: "1-877-991-0909",
      });
    }
  };

  return (
    <footer className="bg-[#091b3e] text-slate-300 font-sans relative overflow-hidden">
      {/* Subtle Background Accent */}
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#091b3e] via-[#e32b22] to-[#091b3e]"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 lg:pt-20 pb-8">
        {/* TOP SECTION: Newsletter CTA */}
        <div className="bg-white/5 border border-white/10 rounded-3xl p-8 sm:p-10 lg:p-12 mb-16 flex flex-col lg:flex-row items-center justify-between gap-8 backdrop-blur-sm">
          <div className="max-w-2xl text-center lg:text-left">
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white mb-3">
              Get the best rental deals in your inbox.
            </h2>
            <p className="text-slate-400 text-base sm:text-lg">
              Subscribe to our newsletter for exclusive offers, travel tips, and
              early access to holiday discounts.
            </p>
          </div>

          <div className="w-full lg:w-auto flex-shrink-0 flex flex-col sm:flex-row items-stretch gap-3 sm:gap-0">
            <div className="relative flex-grow sm:flex-grow-0">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <FaEnvelope className="text-slate-400" />
              </div>
              <input
                type="email"
                placeholder="Enter your email address"
                className="w-full sm:w-72 lg:w-80 bg-white/10 border border-white/20 text-white placeholder-slate-400 text-sm rounded-xl sm:rounded-r-none sm:rounded-l-xl focus:ring-2 focus:ring-[#e32b22] focus:border-transparent block pl-11 p-4 outline-none transition-all"
              />
            </div>
            <button className="bg-[#e32b22] hover:bg-red-700 text-white font-bold py-4 px-8 rounded-xl sm:rounded-l-none sm:rounded-r-xl transition-colors shadow-lg shadow-red-900/20 whitespace-nowrap">
              Subscribe
            </button>
          </div>
        </div>

        {/* MIDDLE SECTION: 4-Column Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-12 lg:gap-8 mb-16">
          {/* Col 1: Brand Info (Takes up 4 cols on large screens) */}
          <div className="lg:col-span-4 flex flex-col items-start">
            <Link
              to="/"
              className="inline-flex items-center justify-center overflow-hidden rounded-2xl bg-white p-2 sm:p-3 shadow-lg mb-6 transition-transform duration-300 hover:scale-105"
            >
              <img
                src={logo}
                alt="USA Car Booking"
                className="h-12 sm:h-14 lg:h-16 w-auto object-contain"
              />
            </Link>
            <p className="text-slate-400 text-sm leading-relaxed mb-6 pr-4">
              USA Car Booking provides independent booking assistance for
              customers looking to arrange car rental reservations, compare
              vehicle categories, and get practical support during the request
              process.
            </p>
          </div>

          {/* Col 2: Explore Links (Takes up 2 cols) */}
          <div className="lg:col-span-2">
            <h3 className="text-white font-bold text-lg mb-6 tracking-wide">
              Explore
            </h3>
            <ul className="space-y-4">
              {exploreLinks.map((link) => (
                <li key={link.label}>
                  <Link
                    to={link.to}
                    className="group flex items-center text-sm text-slate-400 hover:text-[#e32b22] transition-colors"
                  >
                    <FaChevronRight className="w-3 h-3 mr-2 opacity-0 -ml-5 group-hover:opacity-100 group-hover:ml-0 transition-all duration-300" />
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Col 3: Services Links (Takes up 3 cols) */}
          <div className="lg:col-span-3">
            <h3 className="text-white font-bold text-lg mb-6 tracking-wide">
              Our Services
            </h3>
            <ul className="space-y-4">
              {serviceLinks.map((link) => (
                <li key={link.label}>
                  <Link
                    to={link.to}
                    className="group flex items-center text-sm text-slate-400 hover:text-[#e32b22] transition-colors"
                  >
                    <FaChevronRight className="w-3 h-3 mr-2 opacity-0 -ml-5 group-hover:opacity-100 group-hover:ml-0 transition-all duration-300" />
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Col 4: Contact Info (Takes up 3 cols) */}
          <div className="lg:col-span-3">
            <h3 className="text-white font-bold text-lg mb-6 tracking-wide">
              Contact Us
            </h3>
            <ul className="space-y-5">
              <li>
                <a
                  href="tel:+18779910909"
                  onClick={handlePhoneClick}
                  className="flex items-start gap-3 group"
                >
                  <div className="bg-white/10 p-2.5 rounded-lg group-hover:bg-[#e32b22] transition-colors">
                    <FaPhoneAlt className="text-white text-sm" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wider mb-0.5">
                      Call Us
                    </p>
                    <p className="text-sm text-white font-semibold">
                      1-877-991-0909
                    </p>
                  </div>
                </a>
              </li>
              <li>
                <a
                  href="mailto:reservations@usacarbooking.com"
                  className="flex items-start gap-3 group"
                >
                  <div className="bg-white/10 p-2.5 rounded-lg group-hover:bg-[#e32b22] transition-colors">
                    <FaEnvelope className="text-white text-sm" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wider mb-0.5">
                      Email Us
                    </p>
                    <p className="text-sm text-white font-semibold break-all">
                      reservations@usacarbooking.com
                    </p>
                  </div>
                </a>
              </li>
              <li className="flex items-start gap-3">
                <div className="bg-white/10 p-2.5 rounded-lg">
                  <FaMapMarkerAlt className="text-white text-sm" />
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wider mb-0.5">
                    Location
                  </p>
                  <p className="text-sm text-white font-semibold">
                    Serving travelers across the United States
                  </p>
                </div>
              </li>
            </ul>
          </div>
        </div>

        {/* BOTTOM SECTION: Disclaimer & Copyright */}
        <div className="pt-8 border-t border-white/10 flex flex-col gap-6">
          <div className="bg-white/5 border border-white/5 rounded-xl p-6 flex flex-col md:flex-row items-center gap-4 text-center md:text-left">
            <div className="bg-[#e32b22] p-2 rounded-lg flex-shrink-0">
              <svg
                className="w-5 h-5 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <p className="text-xs sm:text-sm text-slate-400 leading-relaxed max-w-5xl">
              <strong className="text-white">
                Independent Service Disclaimer:
              </strong>{" "}
              USA Car Booking is an online platform that helps users compare car
              rental information and connect with booking support. We are not a
              licensed travel provider, nor do we provide our own travel
              services or quotes. While we aim to provide useful availability
              and rental deal information, rates and vehicle availability can
              change at any time and the cheapest rate cannot be guaranteed. We
              act solely as an independent intermediary.
            </p>
          </div>

          <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-slate-500">
            <div className="text-center md:text-left">
              <p>
                &copy; {new Date().getFullYear()} USA Car Booking. All rights
                reserved.
              </p>
              <p className="mt-1 text-xs uppercase tracking-[0.18em] text-slate-400">
                Powered by Travelanzo LLC
              </p>
            </div>
            <ul className="flex flex-wrap justify-center gap-4 sm:gap-6">
              {legalLinks.map((link) => (
                <li key={link.label}>
                  <Link
                    to={link.to}
                    className="hover:text-white transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </footer>
  );
}
